<template>
  <div class="filter_content_container" v-if="category">
    <Breadcrumb class="filter_content_container__breadcrumbs" :links="breadcrumbLinks" />
    <slot name="header"></slot>
    <div>
      <section class="filter_content_container__section">
        <header class="c_wrapper_2022">
          <div
            class="filter_content_container__overview__description c_text"
            v-if="category.filterPageOverviewDescription"
          >
            <p>{{ category.filterPageOverviewDescription }}</p>
          </div>
          <Accordion class="accordion-row" v-if="filters.length > 0" :first-tab-open="false">
            <AccordionTab
              class="filter_content_container__sidebar__filter"
              open
              toggle-self
              v-for="(level1, level1Index) in filters"
              :key="level1Index"
              v-if="level1.children"
            >
              <div
                class="filter_content_container__sidebar__filter__header"
                :class="{
                  'filter_content_container__sidebar__filter__header--selected': level1.children.some(
                    (child) => child.selected
                  ),
                }"
                slot="header"
              >
                <IconSVG
                  class="filter_content_container__sidebar__filter__header__icon"
                  handle="chevron_down"
                  aria-hidden="true"
                />
                <span class="filter_content_container__sidebar__filter__header__label">{{ level1.title }}</span>
              </div>
              <div class="filter_content_container__sidebar__filter__grid-content" slot="content">
                <button
                  class="filter_content_container__sidebar__filter__button c_button--reset"
                  :class="{
                    'filter_content_container__sidebar__filter__button--disabled':
                      totalResultsForFilter(level2.formatted) === 0,
                  }"
                  :disabled="totalResultsForFilter(level2.formatted) === 0"
                  @click="onFilterToggleClick(level1, level2)"
                  v-for="(level2, level2Index) in level1.children"
                  :key="level2Index"
                >
                  <div
                    class="filter_content_container__sidebar__filter__field"
                    :class="{ 'filter_content_container__sidebar__filter__field--checked': level2.selected }"
                  >
                    <div class="filter_content_container__sidebar__filter__field__checkbox">
                      <IconSVG
                        v-show="level2.selected"
                        class="filter_content_container__sidebar__filter__header__icon__check"
                        handle="check"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      class="filter_content_container__sidebar__filter__field__label"
                      v-html="`${level2.title}&nbsp;(${totalResultsForFilter(level2.formatted) || 0})`"
                    ></div>
                  </div>
                </button>
              </div>
            </AccordionTab>

            <Button
              class="filter_content__sidebar c_button--reset"
              @onClick="resetSelectedFilters"
              v-if="formattedFilters.length || dateFilterStartDate !== '' || dateFilterEndDate !== ''"
              unstyle
              reset
              :aria-label="$t('Clear all')"
            >
              <IconSVG handle="circle_close" class="clear_icon_svg" aria-hidden="true" />
            </Button>
          </Accordion>
        </header>
        <!--[Filter Results Here]-->
        <div class="section-grey c_box--padding--bottom c_box--padding--top">
          <div class="c_wrapper_2022">
            <div v-if="['sm', 'md'].includes($mq)">
              <Button
                class="filter_content_container__sidebar__header filter_content_container__sidebar__header--button"
                unstyle
                reset
                @onClick="showFilters"
                v-if="dateFilter || (filters && filters.length)"
              >
                <p class="filter_content_container__sidebar__label">{{ $t('Filters') }}</p>
                <IconSVG
                  class="filter_content_container__sidebar__header--button__arrow"
                  handle="arrow_right"
                  aria-hidden="true"
                />
              </Button>
            </div>
            <div class="filter_content_container__results--wrapper">
              <!--[START]-->
              <div
                :class="[
                  `filter_content_container__results--${resultsView}`,
                  { 'c_grid c_grid--2-3': resultsView === 'tile' },
                ]"
                v-if="filteredEntries.length"
              >
                <FilterResult
                  :layout="resultsView"
                  :entry="entry"
                  :entry-type="resultsEntryType"
                  v-for="(entry, index) in $mq === 'sm' ? filteredEntries : pagedResults"
                  :key="index"
                />
              </div>
              <div v-else-if="filteredEntries.length === 0">
                <p class="filter_content_container__results--no_results_label c_text--graphik_semibold">
                  {{ $t('No results were returned.') }}
                </p>
              </div>
              <!--[END]-->
              <PaginationControl
                class="c_box--margin--top"
                :items-to-page="filteredEntries"
                justify-content="flex-end"
                ref="pagination"
                :results-per-page="['sm', 'md'].includes($mq) ? 10 : 9"
                @pagedItemsUpdated="updatePagedResults"
                v-if="$mq !== 'sm'"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    <!--Nav Category filter -->
    <div class="filter_content_container__filter_nav" v-show="filtersShown" v-if="['sm', 'md'].includes($mq)">
      <div class="filter_content_container__filter_nav__mask c_box--mask c_box--black_mask" @click="hideFilters"></div>
      <div class="filter_content_container__filter_nav__wrapper c_box--mask">
        <div class="filter_content_container__filter_nav__filters_wrapper" v-if="dateFilter">
          <div class="filter_content_container__filter_nav__filter">
            <DatePicker
              ref="datePicker"
              @datePickerOpened="onDatePickerOpened"
              @datePickerUpdated="onDatePickerUpdated"
            />
          </div>
        </div>

        <Button
          class="filter_content_container__filter_nav__back"
          :label-text="$t('< Back')"
          color="black"
          @onClick="hideFilters"
        />

        <div
          class="filter_content_container__filter_nav__filters_wrapper filter_content_container__filter_nav__filters_flex"
        >
          <div class="filter_content_container__sidebar__filter_title">{{ $t('Filters:') }}</div>

          <AccordionTab
            class="filter_content_container__sidebar__filter_side"
            toggle-self
            v-for="(level1, level1Index) in filters"
            :key="level1Index"
            v-if="level1.children"
          >
            <div
              class="filter_content_container__sidebar__filter__header filter_content_container__sidebar__filter__header--flex"
              slot="header"
            >
              <span class="filter_content_container__sidebar__filter__header__label">{{ level1.title }}</span>
              <!-- <IconSVG class="filter_content_container__sidebar__filter__header__icon" handle="plus" aria-hidden="true" /> -->
            </div>
            <div class="filter_content_container__sidebar__filter__flex-content" slot="content">
              <button
                class="filter_content_container__sidebar__filter__button c_button--reset"
                :class="{
                  'filter_content_container__sidebar__filter__button--disabled':
                    totalResultsForFilter(level2.formatted) === 0,
                }"
                :disabled="totalResultsForFilter(level2.formatted) === 0"
                @click="onFilterToggleClick(level1, level2)"
                v-for="(level2, level2Index) in level1.children"
                :key="level2Index"
              >
                <div
                  class="filter_content_container__sidebar__filter__field"
                  :class="{ 'filter_content_container__sidebar__filter__field--checked': level2.selected }"
                >
                  <div class="filter_content_container__sidebar__filter__field__checkbox">
                    <IconSVG
                      v-show="level2.selected"
                      class="filter_content_container__sidebar__filter__header__icon__check"
                      handle="check"
                      aria-hidden="true"
                    />
                  </div>
                  <div
                    class="filter_content_container__sidebar__filter__field__label"
                    v-html="`${level2.title}&nbsp;(${totalResultsForFilter(level2.formatted) || 0})`"
                  ></div>
                </div>
              </button>
            </div>
          </AccordionTab>
        </div>
        <div class="filter_content_container__filter_nav__actions_flex">
          <Button outline :label-text="$t('Reset')" color="black" @onClick="resetSelectedFilters" />
          <Button :label-text="$t('Apply')" @onClick="hideFilters" />
        </div>
      </div>
    </div>
    <MatrixBlockLandingPageContent
      :matrix-blocks="category.landingPageContent"
      :related-posts-ids="relatedPostsIds || []"
      v-if="category.landingPageContent"
    />
  </div>
</template>

<script>
import { log } from 'JS/global.js';
import areRangesOverlapping from 'date-fns/are_ranges_overlapping';
import isWithinRange from 'date-fns/is_within_range';
import format from 'date-fns/format';
import sortBy from 'lodash/sortBy.js';
import Accordion from 'Components/accordion/Accordion.vue';
import AccordionTab from 'Components/accordion/AccordionTab.vue';
import Breadcrumb from 'Components/breadcrumb/Breadcrumb.vue';
import Button from 'Components/button/Button.vue';
import DatePicker from 'Components/date_picker/DatePicker.vue';
import FilterResult from 'Components/New_FilterP/FilterResult.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import PaginationControl from 'Components/pagination_control/PaginationControl.vue';

// Objects in the `resultsEntries` prop are passed through filter functions to get to the results displayed on the page:
// resultsEntries (collection of entries passed in as a prop)
// ▼
// sortByResults (results sorted by `sortByControl` SelectControl)
// ▼
// filteredEntries (collection returned after comparing with selected filter checkboxes, filtering based on date picker, and after search field)
// ▼
// pagedResults (collection returned by PaginationControl)

export default {
  components: {
    Accordion,
    AccordionTab,
    Breadcrumb,
    Button,
    DatePicker,
    FilterResult,
    IconSVG,
    MatrixBlockLandingPageContent: () => import('Components/matrix_blocks/MatrixBlockLandingPageContent.vue'),
    PaginationControl,
  },
  data() {
    return {
      dateFilterEndDate: '',
      dateFilterSelected: '',
      dateFilterStartDate: '',
      filters: [],
      filtersShown: false,
      formattedFilters: [],
      pagedResults: [],
      searchFieldValue: '',
      selectedFilters: [],
      sortBySelection: 'default',
      resultsViewSelected: 'tile', // list, tile,
    };
  },
  props: {
    category: Object,
    dateFilter: { type: Boolean, default: false },
    ignoreLocalStorage: { type: Boolean, default: false },
    relatedPostsIds: Array,
    resultsEntries: Array,
    resultsEntryType: String,
    resultsHeader: String,
    searchField: { type: Boolean, default: false },
    seomatic: Object,
    sortByAttributes: Array,
    sortByAdditionalOptions: Array,
    sortByDefault: { type: String, default: 'titleAZ' },
    viewOptions: { type: Boolean, default: false },
  },
  computed: {
    breadcrumbLinks() {
      // Set link_field in revers order to make it easier to work with graphql data, then reverse the array at the end
      const links = [{ label: this.category.title }];

      if (this.category) {
        if (this.category.parent) {
          let link = this.category.parent;
          links.push({ label: link.title, href: link.breadcrumbLink.length ? link.breadcrumbLink[0].uri : link.uri });

          if (link.parent) {
            link = link.parent;
            links.push({ label: link.title, href: link.breadcrumbLink.length ? link.breadcrumbLink[0].uri : link.uri });

            if (link.parent) {
              link = link.parent;
              links.push({
                label: link.title,
                href: link.breadcrumbLink.length ? link.breadcrumbLink[0].uri : link.uri,
              });

              if (link.parent) {
                link = link.parent;
                links.push({
                  label: link.title,
                  href: link.breadcrumbLink.length ? link.breadcrumbLink[0].uri : link.uri,
                });

                if (link.parent) {
                  link = link.parent;
                  links.push({
                    label: link.title,
                    href: link.breadcrumbLink.length ? link.breadcrumbLink[0].uri : link.uri,
                  });
                }
              }
            }
          }
        }
        links.push({ label: this.$t('Home'), href: '/' });
      }

      return links.reverse();
    },
    areFiltersSelected() {
      return this.category.filters.some((filter) => filter.selected);
    },
    filteredEntries() {
      if (this.sortByResults) {
        let returnEntries = this.sortByResults;
        if (this.sortByResults?.length) {
          const entries = [];
          this.sortByResults.forEach((entry) => {
            // Match entry filters with selected filters
            const intersection = this.formattedFilters.every((elem) => entry.formattedFilters.includes(elem));

            // See if entry
            let rangesOverlap = false;
            let withinDateRange = true;
            if (this.dateFilterStartDate || this.dateFilterEndDate) {
              withinDateRange = false;
            }
            if (
              this.dateFilter &&
              entry.filterByDate &&
              this.$refs.datePicker &&
              (this.dateFilterStartDate !== '' || this.dateFilterEndDate !== '')
            ) {
              if (entry.startDate && entry.endDate) {
                withinDateRange = false;
                rangesOverlap = areRangesOverlapping(
                  format(entry.startDate, 'YYYY-MM-DD'),
                  format(entry.endDate, 'YYYY-MM-DD'),
                  format(this.$refs.datePicker.startDate, 'YYYY-MM-DD'),
                  format(this.$refs.datePicker.endDate, 'YYYY-MM-DD')
                );

                if (!rangesOverlap) {
                  // If entry start date is equal to datepicker start or end date.
                  if (
                    format(entry.startDate, 'YYYY-MM-DD') === format(this.$refs.datePicker.startDate, 'YYYY-MM-DD') ||
                    format(entry.startDate, 'YYYY-MM-DD') === format(this.$refs.datePicker.endDate, 'YYYY-MM-DD')
                  ) {
                    rangesOverlap = true;
                  }
                  // If entry end date is equal to datepicker start or end date.
                  if (
                    format(entry.endDate, 'YYYY-MM-DD') === format(this.$refs.datePicker.startDate, 'YYYY-MM-DD') ||
                    format(entry.endDate, 'YYYY-MM-DD') === format(this.$refs.datePicker.endDate, 'YYYY-MM-DD')
                  ) {
                    rangesOverlap = true;
                  }
                }
              } else {
                const date = new Date(entry.filterByDate);
                withinDateRange = isWithinRange(
                  format(date, 'YYYY-MM-DD'),
                  format(this.$refs.datePicker.startDate, 'YYYY-MM-DD'),
                  format(this.$refs.datePicker.endDate, 'YYYY-MM-DD')
                );
                if (!withinDateRange) {
                  if (
                    format(date, 'YYYY-MM-DD') === format(this.$refs.datePicker.startDate, 'YYYY-MM-DD') ||
                    format(date, 'YYYY-MM-DD') === format(this.$refs.datePicker.endDate, 'YYYY-MM-DD')
                  ) {
                    withinDateRange = true;
                  }
                }
              }
            }

            if (intersection && (withinDateRange || rangesOverlap)) {
              entries.push(entry);
            }
          });
          returnEntries = entries;
        }

        if (this.searchFieldValue !== '') {
          return returnEntries.filter(
            (item) => item.title.toLowerCase().search(this.searchFieldValue.toLowerCase()) > -1
          );
        } else {
          return returnEntries;
        }
      }

      return [];
    },
    resultsView() {
      if (['md', 'sm'].includes(this.$mq)) {
        return 'tile';
      }
      return this.resultsViewSelected;
    },
    sortByOptions() {
      const options = [];

      options.push({ label: this.$t('Sort by'), value: 'default' });

      if (this.sortByAdditionalOptions) {
        if (this.sortByAdditionalOptions.includes('titleAZ')) {
          options.push({ label: this.$t('Title (A-Z)'), value: 'sort-by-titleAZ' });
        }
        if (this.sortByAdditionalOptions.includes('titleZA')) {
          options.push({ label: this.$t('Title (Z-A)'), value: 'sort-by-titleZA' });
        }
        if (this.sortByAdditionalOptions.includes('postDateNewestFirst')) {
          options.push({ label: this.$t('Date (Newest first)'), value: 'sort-by-postDateNewestFirst' });
        }
        if (this.sortByAdditionalOptions.includes('postDateOldestFirst')) {
          options.push({ label: this.$t('Date (Oldest first)'), value: 'sort-by-postDateOldestFirst' });
        }
      }

      if (this.sortByAttributes) {
        for (let i = 0; i < this.sortByAttributes.length; i++) {
          options.push({ label: this.sortByAttributes[i].label, value: `sort-by-attribute-${i}` });
        }
      }

      return options;
    },
    sortByResults() {
      let results = false;
      let sortingAttributeIndex = false;
      let sortNumerically = false;
      let sortingMethod = false;

      // Determine the sort by method
      if (this.sortBySelection === 'default') {
        switch (this.sortByDefault) {
          case 'titleAZ':
            sortingMethod = 'titleAZ';
            break;
          case 'titleZA':
            sortingMethod = 'titleAZ';
            break;
          case 'postDateNewestFirst':
            sortingMethod = 'postDateNewestFirst';
            break;
          case 'postDateOldestFirst':
            sortingMethod = 'postDateOldestFirst';
            break;
          case 'sortingAttributesSortByTheFirstItemInTheList':
            sortingMethod = 'sortingAttributes';
            sortingAttributeIndex = 0;
            break;
        }
      } else {
        switch (this.sortBySelection) {
          case 'sort-by-titleAZ':
            sortingMethod = 'titleAZ';
            break;
          case 'sort-by-titleZA':
            sortingMethod = 'titleZA';
            break;
          case 'sort-by-postDateNewestFirst':
            sortingMethod = 'postDateNewestFirst';
            break;
          case 'sort-by-postDateOldestFirst':
            sortingMethod = 'postDateOldestFirst';
            break;
          default:
            sortingMethod = 'sortingAttributes';
        }
      }

      // Sort results
      if (sortingMethod && this.resultsEntries) {
        switch (sortingMethod) {
          case 'titleAZ':
            results = this.resultsEntries.slice();
            break;
          case 'titleZA':
            results = this.resultsEntries.slice().reverse();
            break;
          case 'postDateNewestFirst':
            results = sortBy(this.resultsEntries, ['sortByDate']);
            break;
          case 'postDateOldestFirst':
            results = sortBy(this.resultsEntries, ['sortByDate']).reverse();
            break;
          case 'sortingAttributes':
            if (this.sortBySelection.startsWith('sort-by-attribute-')) {
              const attributeIndex = this.sortBySelection.substr(18);

              if (this.sortByAttributes[attributeIndex] !== undefined) {
                sortingAttributeIndex = attributeIndex;
                sortNumerically = this.sortByAttributes[attributeIndex].sortValuesNumerically;
              }
            }
            if (this.sortByAttributes && sortingAttributeIndex !== undefined) {
              const attribute = this.sortByAttributes[sortingAttributeIndex];

              const sortedByAttribute = sortBy(this.resultsEntries, [
                (entry) => {
                  if (entry.entryAttributes) {
                    const matchedAttribute = entry.entryAttributes.find((item) =>
                      item.attribute ? attribute.attribute === item.attribute : false
                    );

                    if (sortNumerically) {
                      // replace comma with nothing - this is making the assumption number will be entered with an American numeric value
                      return matchedAttribute && matchedAttribute.value
                        ? parseFloat(matchedAttribute.value.replace(',', ''))
                        : null;
                    } else {
                      return matchedAttribute && matchedAttribute.value ? matchedAttribute.value : null;
                    }
                  }
                  return '';
                },
                'title',
              ]);

              results = attribute?.order === 'descending' ? sortedByAttribute.reverse() : sortedByAttribute;
            }
            break;
        }

        if (results) {
          return results;
        }
      }

      return this.resultsEntries?.slice();
    },
  },
  methods: {
    formatFilters() {
      // Format filter categories from GraphQL into JS object
      log('Format Filters Called');
      let currentLevel1Index = 0;
      log('category', this.category);
      log('category filters', this.category.filters);
      if (this.category.filters) {
        log('category exists');
        this.category.filters.forEach((filter) => {
          if (filter.level === 1) {
            // Add top-level filters to filters array
            this.filters.push(filter);
            currentLevel1Index = this.filters.length - 1;
            this.filters[currentLevel1Index].children = [];
          } else {
            // Set initial selected state based on whether or not filter is in the URL query string
            let selected = false;
            if (this.$route.query && filter.parent) {
              if (this.$route.query[filter.parent.slug]) {
                selected = this.$route.query[filter.parent.slug].split(',').includes(filter.slug);
              }
            }
            this.$set(filter, 'selected', selected);

            // Format filters for easier matching against results entries
            this.$set(filter, 'formatted', `${filter.parent.slug}:${filter.slug}`);

            // Add filter to parent filters
            this.filters[currentLevel1Index].children.push(filter);
          }
          log('Format Filters right before update selected');
          this.updateSelectedFilters();
        });
      } else {
        log('category doesnt exist');
      }
    },
    hideFilters() {
      this.filtersShown = false;
    },
    onDatePickerOpened() {
      this.dateFilterSelected = '';
    },
    onDatePickerUpdated(startDate, endDate) {
      this.dateFilterStartDate = startDate;
      this.dateFilterEndDate = endDate;
    },
    onFilterToggleClick(level1, level2) {
      // Toggle the selected state for level1 and level2
      level2.selected = !level2.selected;

      // Update list of selected filters to include new states
      this.updateSelectedFilters();

      // Update URL query string based on selected filters
      this.updateUrlQuery();
    },

    onSortByFieldUpdated(newValue) {
      this.sortBySelection = newValue;
      if (this.$mq !== 'sm' && this.$refs?.pagination) {
        this.$refs.pagination.reset();
      }
    },
    onTileViewButtonClick(newState) {
      this.resultsViewSelected = newState;
      localStorage.setItem('filter:view', newState);
    },
    resetDateFilter() {
      this.dateFilterSelected = '';
      this.$refs.datePicker.resetDates();
    },
    resetSelectedFilters() {
      // Clears out the filters
      this.category.filters.forEach((filter) => {
        if (filter.level === 2) {
          filter.selected = false;
        }
      });
      if (this.dateFilterSelected || this.dateFilterStartDate || this.dateFilterEndDate) {
        this.resetDateFilter();
      }
      this.updateSelectedFilters();
      this.updateUrlQuery();
    },
    selectDateFilter(handle) {
      this.dateFilterStartDate = '';
      this.dateFilterEndDate = '';

      switch (handle) {
        case 'today':
          if (this.dateFilterSelected !== 'today') {
            this.dateFilterSelected = 'today';
            this.$refs.datePicker.selectToday();
          } else {
            this.resetDateFilter();
          }
          break;
        case 'thisWeek':
          if (this.dateFilterSelected !== 'thisWeek') {
            this.dateFilterSelected = 'thisWeek';
            this.$refs.datePicker.selectThisWeek();
          } else {
            this.resetDateFilter();
          }
          break;
        case 'nextWeek':
          if (this.dateFilterSelected !== 'nextWeek') {
            this.dateFilterSelected = 'nextWeek';
            this.$refs.datePicker.selectNextWeek();
          } else {
            this.resetDateFilter();
          }
          break;
        case 'thisMonth':
          if (this.dateFilterSelected !== 'thisMonth') {
            this.dateFilterSelected = 'thisMonth';
            this.$refs.datePicker.selectThisMonth();
          } else {
            this.resetDateFilter();
          }
          break;
        case 'nextMonth':
          if (this.dateFilterSelected !== 'nextMonth') {
            this.dateFilterSelected = 'nextMonth';
            this.$refs.datePicker.selectNextMonth();
          } else {
            this.resetDateFilter();
          }
          break;
      }
    },
    showFilters() {
      this.filtersShown = true;
    },
    totalResultsForFilter(filter) {
      if (this.filteredEntries?.length) {
        return this.filteredEntries.filter((item) => item.formattedFilters?.includes(filter))?.length;
      }
      return 0;
    },
    updatePagedResults() {
      this.pagedResults = this.$refs.pagination.currentPageResults;
    },
    updateSelectedFilters() {
      log('update Selected Filters Called');
      const formattedFilters = [];
      const selectedFilters = {};
      if (this.category.filters) {
        this.category.filters.forEach((item) => {
          if (item.selected && item.level !== 1) {
            // Set string-based filters to match against entries filters
            const formattedFilter = `${item.parent.slug}:${item.slug}`;
            if (!formattedFilters.includes(formattedFilter)) {
              formattedFilters.push(formattedFilter);
            }

            // Set filters to update the URL
            if (selectedFilters[item.parent.slug]) {
              selectedFilters[item.parent.slug].push(item.slug);
            } else {
              selectedFilters[item.parent.slug] = [item.slug];
            }
          }
        });
      }
      this.formattedFilters = formattedFilters;
      this.selectedFilters = selectedFilters;
      if (this.$mq !== 'sm' && this.$refs?.pagination) {
        this.$refs.pagination.reset();
      }
    },
    updateUrlQuery() {
      const query = [];
      if (this.selectedFilters && Object.keys(this.seomatic).length) {
        if (Object.keys(this.selectedFilters).length) {
          Object.keys(this.selectedFilters).forEach((key) => {
            query[key] = this.selectedFilters[key].join(',');
          });
        }

        if (Object.keys(query).length) {
          const queryString = Object.keys(query).length
            ? '?' +
              Object.keys(query)
                .map((key) => key + '=' + query[key])
                .join('&')
            : '';
          history.pushState({}, this.seomatic.title, `${this.$route.path}${queryString}`);
        } else if (Object.keys(this.$route.query)) {
          history.pushState({}, this.seomatic.title, this.$route.path);
        }
      }
    },
    updateResults() {
      this.category.filters.forEach((filter) => {
        let selected = false;
        if (this.$route.query && filter.parent) {
          if (this.$route.query[filter.parent.slug]) {
            selected = this.$route.query[filter.parent.slug].split(',').includes(filter.slug);
          }
        }
        this.$set(filter, 'selected', selected);
      });
      this.updateSelectedFilters();
    },
  },
  watch: {
    $route(to, from) {
      this.updateResults();
    },
  },
  mounted() {
    this.formatFilters();
    if (localStorage.getItem('filter:view') && !this.ignoreLocalStorage) {
      this.resultsViewSelected = localStorage.getItem('filter:view');
    }
  },
};
</script>

<style lang="scss">
.filter_content_container {
  $self: &;

  @at-root #{$self}__breadcrumbs {
    margin: 13px 0;
  }
  @at-root #{$self}__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    grid-gap: 10px;

    @include mq($mq_md_to_lg) {
      grid-template-columns: 277px 1fr;
    }
  }
  @at-root #{$self}__sidebar {
    @include mq($mq_sm_to_md) {
      grid-column: 1;
      grid-row: 1;
    }
    @at-root #{&}__header {
      margin: 0 0 px_to_rem(30);
      color: $color_gray_86;

      @at-root #{&}--button {
        display: flex;
        flex-flow: row wrap;
        align-items: center;
        padding-right: 50px;
        position: relative;

        &:hover,
        &:focus {
          color: $color_red;
        }
        @at-root #{&}__arrow {
          padding-left: px_to_rem(5);
          height: px_to_rem(14);
          width: px_to_rem(10);
          background-position: 50%;
        }
        @media screen and (min-width: 768px) {
          display: none;
        }
      }
    }
    @at-root #{&}__label {
      @include font('graphik_semibold');
      font-size: 0.9rem;
      text-transform: uppercase;

      @include mq_max($mq_md_to_lg) {
        display: inline-block;
        position: relative;
        font-size: 1.1rem;
      }
    }
    @at-root #{&}__clear {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      text-transform: uppercase;
      font-size: 0.85rem;
      color: $color_gray_86;
      cursor: pointer;
      .c_icon_svg {
        padding-right: px_to_rem(5);
        color: $color_yellow;
        svg {
          vertical-align: top;
        }
      }
      &:hover,
      &:focus {
        color: $color_black;
        text-decoration: underline;
      }
    }
    @at-root #{&}__filter {
      $filter: &;

      border: 1px solid black;
      border-radius: 8px;
      width: unset;
      &--nav {
        border: none;
        width: unset;
        text-align: left;
      }

      @at-root #{&}__header {
        display: grid;
        grid-template-columns: 25px 1fr;
        gap: 1px;
        align-items: start;
        padding: 13px;
        width: 100%; // Set the width to 100% or adjust as needed
        border-radius: 8px;
        text-transform: uppercase;
        @include font('graphik_semibold');
        font-size: 1.18rem;
        line-height: 1.3;
        &:hover,
        &:focus {
          color: $color_red;
        }

        &--flex {
          display: flex;
          justify-content: space-between;
        }

        &--selected {
          background-color: var(--color-yellow);
        }

        @at-root #{&}__icon {
          // transition: transform .3s ease-out;
          grid-column: 1;
          grid-row: 1;
          max-width: 15px;
          color: black;
          font-size: 12px;
        }
        &__check {
          grid-column: 1;
          grid-row: 1;
          max-width: 15px;
          color: black;
          padding-top: 5px;
          display: grid;
        }
        @at-root #{&}__label {
          grid-column: 2;
          grid-row: 1;
          display: inline-block;
          color: black;
          font-size: 12px;
        }
      }
      @at-root #{&}__grid-content {
        padding: 16px;
        // border-top: 1px solid $color_gray_191;
        position: absolute;
        width: 250px;
        border: 1px solid rgb(0, 0, 0);
        background-color: #fff;
        margin-top: 8px;
        z-index: 999;
        font-size: 0.9rem;
      }
      @at-root #{&}__flex-content {
        padding: 16px;
      }
      @at-root #{&}__button {
        $button: &;

        width: 100%;

        @at-root #{$button} + #{$button} {
          margin-top: 0.5rem;
        }

        @at-root #{&}--disabled {
          opacity: 0.3;
        }
      }
      @at-root #{&}__field {
        $field: &;
        display: grid;
        grid-template-columns: 15px 1fr;
        gap: 16px;
        align-items: start;
        width: 100%;
        @include font('graphik_light');
        // line-height: 1.3;
        @at-root #{$filter}__button:not(#{$filter}__button--disabled) {
          &:hover,
          &:focus {
            #{$field} {
              text-decoration: underline;
            }
          }
        }

        @at-root #{&}__checkbox {
          display: flex;
          align-items: center;
          border: 1.5px solid $color_black;
          border-radius: 4px;
          width: 15px;
          height: 15px;
          grid-column: 1;
          grid-row: 1;

          @at-root #{$field}--checked & {
            background-color: $color_yellow;
          }
        }
        @at-root #{&}__label {
          display: inline-block;
          grid-column: 2;
          grid-row: 1;
        }
      }
    }
    @at-root #{&}__date_picker {
      margin-top: 0.6rem;
    }
  }
  @at-root #{$self}__section {
    grid-column: 1;
    grid-row: 1;
    @include mq($mq_md_to_lg) {
      grid-column: 2;
      grid-row: 1;
    }
  }
  @at-root #{$self}__filter_nav {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: $z_overlay;

    @at-root #{&}__wrapper {
      width: calc(100% - 100px);
      right: 0;
      left: auto;
      background-color: #d3d3d3;
      overflow: auto;
    }
    @at-root #{&}__filters_wrapper {
      padding: 0 20px 20px;
    }
    @at-root #{&}__filters_grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      gap: 20px;
    }
    @at-root #{&}__filters_flex {
      display: flex;
      flex-direction: column;

      .c_accordion_tab {
        .c_accordion_tab__header {
          position: relative;

          &::before {
            content: '+';
            position: absolute;
            right: 0;
            font-family: 'Graphik-Semibold', Helvetica, Arial, sans-serif;
            font-size: 2rem;
            top: 50%;
            transform: translateY(-50%);
          }
        }
      }

      .c_accordion_tab--open {
        .c_accordion_tab__header::before {
          content: '-';
        }
      }
    }
    @at-root #{&}__filters {
      grid-column: 1 / 3;
    }
    @at-root #{&}__filter {
      padding: 0.8rem 0 0.7rem;
      border-bottom: 1px solid $color_black;

      &:last-child {
        padding-bottom: 0;
        border-bottom: none;
      }

      @at-root #{&}__group {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        grid-auto-rows: 50px;
        gap: 1px;
        margin-top: 0.5rem;
      }
      @at-root #{&}__button {
        display: block;
        padding: 0.5rem;
        background-color: $color_gray_235;
        text-align: center;
        font-size: 0.8rem;
        line-height: 1.1;

        &:disabled {
          color: $color_gray_191;
        }

        @at-root #{&}--active {
          background-color: $color_black;
          color: $color_white;
        }
      }
    }
    @at-root #{&}__actions_flex {
      display: flex;
      padding: 0 20px 20px;
      gap: 20px;
    }
    @at-root #{&}__back {
      max-width: 100%;
      border-radius: 0;
      justify-content: flex-start;
      padding: 24px 16px;
      text-transform: none;
    }
  }
  @at-root #{$self}__sidebar__filter_title {
    font-family: 'Graphik-Semibold', Helvetica, Arial, sans-serif;
    border-bottom: 1px solid #fff;
    padding: 24px 0;
  }
  @at-root #{$self}__sidebar__filter_side {
    border-bottom: 1px solid #fff;
    padding: 0;
  }
  @at-root #{$self}__overview__description {
    margin-top: 0;
    @include mq(600px){
      margin-top: px_to_rem(20);
    }
  }
  @at-root #{$self}__view_options {
    margin: 1.6rem 0;
    height: 53px;
    border: 1px solid $color_gray_191;

    @at-root #{&}:first-child {
      margin-top: 0;
    }

    @at-root #{&}__view {
      display: grid;
      grid-gap: 20px;
      grid-template-columns: auto 30px 30px;
      grid-template-rows: 100%;
      align-items: center;
      padding: 0 1.25rem;
      text-transform: uppercase;

      @at-root .app--isEdge15orDown & {
        display: flex;
        flex-flow: row nowrap;
        & > * {
          flex: 0 1 50%;
        }
      }
      @include font('graphik_semibold');
      color: $color_black;
    }
    @at-root #{&}__button {
      height: 30px;
      outline: none;
      opacity: 0.4;
      transition: opacity $anim_duration_fade ease-out;

      @at-root #{&}--active {
        opacity: 1;
      }

      &:hover,
      &:focus {
        color: $color_red;
      }

      & > * {
        height: 100%;
      }
    }
  }
  @at-root #{$self}__search {
    display: grid;
    grid-template-columns: 1fr 27px;
    grid-template-rows: auto;
    gap: 15px;
    padding-right: 15px;
    width: 100%;
    height: 100%;
    color: $color_gray_86;
    box-sizing: border-box;

    @at-root #{&}__field {
      grid-column: 1;
      grid-row: 1;
      width: 100% !important;
      height: 100% !important;
      border: none !important;
      @include font('graphik_bold', true);
      appearance: none;

      &::placeholder {
        color: $color_gray_86 !important;
      }
    }
    .c_icon_svg {
      grid-column: 2;
      grid-row: 1;
    }
  }
  @at-root #{$self}__results_header {
    @at-root #{&}__header {
      margin-top: 20px;
      padding: 10px 0;
    }
  }
  @at-root #{$self}__results--no_results_label {
    padding-top: 25px;
    padding-bottom: 25px;
    color: $color_gray_86;
    text-align: center;
  }

  @at-root #{$self}__result {
    $result: &;
    $_padding: 17px;
    display: grid;
    grid-gap: 10px;
    padding: $_padding;
    position: relative;
    background-color: $color_white;
    border: 1px solid $color_gray_235;
    box-shadow: 3px 3px 8px $color_shadow;

    @at-root #{$self}__results--tile {
      grid-template-columns: repeat(1, 1fr) !important;
      @media (min-width: 800px) and (max-width: 1300px) {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      @include mq($mq_md_to_lg) {
        grid-template-columns: repeat(3, 1fr) !important;
      }
      & {
        & > * {
          margin-bottom: 0px;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
    @at-root #{$self}__results--list & {
      margin-bottom: 21px;
      display: grid;
      grid-template-columns: 240px 200px auto;
      grid-template-rows: 111px 111px;
      grid-gap: $_padding;
      grid-template-areas:
        'image info info'
        'image brochure buttons';
    }

    @at-root #{$result}__image {
      @at-root #{$self}__results--list & {
        grid-area: image;
      }
      @at-root #{$self}__results--tile & {
        margin-top: -$_padding;
        margin-left: -$_padding;
        height: 100px;
        width: calc(100% + #{$_padding * 2});
        padding-top: 100px;
      }
    }
    @at-root #{$result}__info {
      @at-root #{$self}__results--list & {
        grid-area: info;
      }
    }
    @at-root #{$result}__brochure {
      @at-root #{$self}__results--list & {
        grid-area: brochure;
        align-self: end;
      }
    }
    @at-root #{$result}__buttons {
      @at-root #{$self}__results--list & {
        grid-area: buttons;
        align-self: end;
      }
    }
    @at-root #{$result}__buttons_wrapper {
      @at-root #{$self}__results--tile & {
        & > * {
          margin-bottom: 8px;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
    @media (min-width: 699px) {
      .c_grid:not(.c_grid--js).c_grid--2-3 {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }
  .section-grey {
    background-color: $color_gray_240;
    @media (max-width: 767px) {
      margin-top: px_to_rem(30);
    }
  }
  .accordion-row {
    padding: px_to_rem(30) 0;
    display: flex;
    justify-content: left;
    gap: 16px;
    flex-wrap: wrap;
  }
  .filter-mobile {
    padding: 1px 6vw;
  }

  @media screen and (max-width: 767px) {
    .accordion-row {
      display: none;
    }
  }
  .clear_icon_svg {
    padding-right: px_to_rem(5);
    color: $color_black;
    height: 35px;
    width: 35px;

    &:hover {
      color: #1c1c1c;
      text-decoration: underline;
    }
    svg {
      vertical-align: top;
    }
  }
  .clear-container {
    display: inline-flex;
    align-items: center;
  }

  .clear-text {
    margin-left: 5px; /* Adjust as needed */
    text-align: right;
  }
}
</style>
